import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  Only2PhotosInitModel,
  Only2PhotosInitVariables,
  Only2PhotosModel,
  Only2PhotosVariables,
} from '../../../../models/email-template/only-2-photos.interface';

/**
 * Mockup-styled Only2Photos block - a drop-in replacement for
 * app-only-2-photos in block-renderer-v2 (same [block]/[isPreview] inputs and
 * outputs), so it covers both the center-canvas read-only summary
 * (isPreview=true) and the right-panel edit form (isPreview=false). Reuses
 * the real, unmodified app-block-text (image upload/segment/validation),
 * app-field-editor-v2 and app-pin sub-components and the exact same
 * Only2PhotosVariables field names - only the surrounding layout is new. Two
 * side-by-side images, nothing else - no heading/CTA per card, unlike
 * Inspiration/2x Property (see x2-prop-rec-v2 for that fuller pattern).
 */
@Component({
  selector: 'app-only-2-photos-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineMediaV2Directive],
  templateUrl: './only-2-photos-v2.component.html',
  styleUrl: './only-2-photos-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Only2PhotosComponentV2 extends BaseComponent implements OnInit {
  @Input() block: Only2PhotosModel = new Only2PhotosInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new Only2PhotosInitVariables();
    }
  }

  firstImagePlaceholder = 'First Image';
  secondImagePlaceholder = 'Second Image';
  firstImageHrefPlaceholder = 'Enter First Image Redirect Link';
  secondImageHrefPlaceholder = 'Enter Second Image Redirect Link';
  firstImageAltPlaceholder = 'Enter First Image Alt Text';
  secondImageAltPlaceholder = 'Enter Second Image Alt Text';

  protected image1AsUpload = true;
  protected image2AsUpload = true;

  protected toggleImage1Mode(): void {
    this.image1AsUpload = !this.image1AsUpload;
  }

  protected toggleImage2Mode(): void {
    this.image2AsUpload = !this.image2AsUpload;
  }

  private hrefFor(link?: string, segment?: string): string {
    const base = this.helper.getFullUrl(link);
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  get firstImageHref(): string {
    return this.hrefFor(this.block.variables?.['FirstImageHref'], this.block.variables?.['FirstImageHrefSegmentRegex']);
  }

  get secondImageHref(): string {
    return this.hrefFor(this.block.variables?.['SecondImageHref'], this.block.variables?.['SecondImageHrefSegmentRegex']);
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof Only2PhotosVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
